import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Home/Footer';
import BrowserNotSupported from './BrowserNotSupported';
import ScoreCard from '../components/IELTS/ScoreCard';
import TestCard from '../components/IELTS/TestCard';
import SectionCard from '../components/IELTS/SectionCard';
import SectionalTestCard from '../components/IELTS/SectionalTestCard';
import {
  CalculatorIcon,
  CalendarIcon,
  ListeningIcon,
  ReadingIcon,
  SpeakingIcon,
  TargetIcon,
  WritingIcon,
} from '../components/IELTS/icons';
import { sections, testFilters } from '../components/IELTS/mockData';
import { Base_URL } from '../Client/apiURL';
import { themeColors } from '../components/IELTS/styles/theme';
import { formatIELTSBandScore } from '../components/IELTS/utils/scoreHelpers';

const isNotChrome = () => {
  const userAgent = navigator.userAgent;
  return !/Chrome/.test(userAgent) || /OPR|Edge/.test(userAgent);
};

const PageShell = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fbff 0%, #f7f1ff 48%, #fffaf2 100%)',
  paddingTop: '82px',
  [theme.breakpoints.down('md')]: {
    paddingTop: '72px',
  },
}));

const DashboardContainer = styled(Container)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '220px minmax(0, 1fr)',
  gap: '16px',
  paddingBottom: '28px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
}));

const SidebarCard = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: '88px',
  alignSelf: 'start',
  borderRadius: '18px',
  padding: '12px',
  border: '1px solid rgba(153, 108, 254, 0.14)',
  boxShadow: '0 14px 30px rgba(41, 22, 89, 0.07)',
  [theme.breakpoints.down('md')]: {
    position: 'static',
  },
}));

const MainCard = styled(Paper)({
  borderRadius: '20px',
  padding: '18px',
  border: '1px solid rgba(153, 108, 254, 0.12)',
  boxShadow: '0 14px 34px rgba(41, 22, 89, 0.07)',
  background: 'rgba(255, 255, 255, 0.94)',
});

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: '12px',
  padding: '9px 11px',
  marginBottom: '6px',
  textTransform: 'none',
  fontSize: '0.9rem',
  fontWeight: 800,
  color: active ? '#fff' : '#3a2f55',
  background: active ? 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)' : 'transparent',
  boxShadow: active ? '0 8px 18px rgba(153, 108, 254, 0.24)' : 'none',
  '&:hover': {
    background: active ? 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)' : '#f5f0ff',
  },
}));

const GroupCard = styled(Paper)({
  borderRadius: '15px',
  padding: '14px',
  marginBottom: '12px',
  border: '1px solid #edf0f7',
  boxShadow: '0 6px 18px rgba(29, 22, 54, 0.045)',
});

const TestGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px',
  marginTop: '12px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const FullTestItem = styled(Box)({
  minWidth: 0,
});

const kindMap = {
  Cambridge: 'cambridge',
  'British Council': 'british council',
  IDP: 'idp',
};

function getGroupId(test) {
  return test?.groupId || test?.group_id || test?.groupID || null;
}

function getStoredGroupName(test) {
  return test?.group_name || test?.groupName || '';
}

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function getGroupPrefix(groupId) {
  if (!groupId) return 'individual';
  const raw = String(groupId).trim();
  const digits = raw.replace(/\D/g, '');
  return (digits || raw).slice(0, 2);
}

function getKindLabel(test) {
  const explicitKind = test?.test_kind || test?.testKind;
  if (explicitKind) {
    const normalized = String(explicitKind).toLowerCase();
    if (normalized === 'idp') return 'IDP';
    if (normalized === 'british council') return 'British Council';
    if (normalized === 'cambridge') return 'Cambridge';
    return titleCase(explicitKind);
  }

  const groupName = getStoredGroupName(test);
  const lowerName = groupName.toLowerCase();
  if (lowerName.includes('cambridge')) return 'Cambridge';
  if (lowerName.includes('idp')) return 'IDP';
  if (lowerName.includes('british council')) return 'British Council';

  const withoutNumbers = groupName.replace(/\d+/g, '').trim();
  return titleCase(withoutNumbers) || 'Group';
}

function getGroupKey(test, includeIndividual) {
  const rawGroupId = getGroupId(test);
  if (!rawGroupId && !includeIndividual) return null;
  if (!rawGroupId) return 'individual';
  const prefix = getGroupPrefix(rawGroupId);
  return `${getKindLabel(test).toLowerCase()}-${prefix}`;
}

function getGroupDisplayName(groupKey, groupTests) {
  if (groupKey === 'individual') return 'Individual Tests';
  const first = groupTests[0] || {};
  return `${getKindLabel(first)} ${getGroupPrefix(getGroupId(first))}`;
}

function getFullTestName(test, seriesName) {
  return getStoredGroupName(test) || `${seriesName} - Test`;
}

function getFullTestSortNumber(fullTest) {
  const namedMatch = String(fullTest.groupName || '').match(/test\s*(\d+)/i);
  if (namedMatch) return Number(namedMatch[1]);

  const groupIdDigits = String(fullTest.groupId || '').replace(/\D/g, '');
  if (groupIdDigits.length > 2) return Number(groupIdDigits.slice(2)) || 0;
  return Number(groupIdDigits) || 0;
}

function getTestName(test) {
  return test?.test_name || test?.name || test?.title || `Test ${test?.id || ''}`;
}

function getTestTypeLabel(test) {
  if (test?.test_type === 'full' || test?.full_test_attempt_id) return 'Full Test';
  return test?.category_name || 'Sectional';
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
}

function formatTime(totalTime, fallback = '00:30m') {
  if (!totalTime) return fallback;
  if (totalTime >= 60) return `${Math.floor(totalTime / 60)}h:${String(totalTime % 60).padStart(2, '0')}m`;
  return `00:${String(totalTime).padStart(2, '0')}m`;
}

function formatResultScore(test) {
  const score = test?.band_score ?? test?.score ?? test?.totalMarksObtained ?? test?.total_marks_obtained;
  return formatIELTSBandScore(score);
}

function groupByPrefix(items, includeIndividual = false) {
  const groups = new Map();
  items.forEach((test) => {
    const key = getGroupKey(test, includeIndividual);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(test);
  });

  return Array.from(groups.entries()).map(([groupKey, groupTests]) => {
    const representative = groupTests[0] || {};
    const rawGroupIds = [...new Set(groupTests.map(getGroupId).filter(Boolean))];
    const primaryGroupId = getGroupId(representative) || rawGroupIds[0] || groupKey;
    return {
      groupKey,
      groupId: primaryGroupId,
      groupPrefix: getGroupPrefix(primaryGroupId),
      rawGroupIds,
      groupName: getGroupDisplayName(groupKey, groupTests),
      tests: groupTests,
    };
  });
}

function groupFullTestsBySeries(items) {
  const seriesMap = new Map();

  items.forEach((test) => {
    const groupId = getGroupId(test);
    const seriesKey = getGroupKey(test, false);
    if (!groupId || !seriesKey) return;

    if (!seriesMap.has(seriesKey)) {
      seriesMap.set(seriesKey, {
        groupKey: seriesKey,
        groupPrefix: getGroupPrefix(groupId),
        groupName: getGroupDisplayName(seriesKey, [test]),
        fullTestsMap: new Map(),
      });
    }

    const series = seriesMap.get(seriesKey);
    const fullTestKey = String(groupId);
    if (!series.fullTestsMap.has(fullTestKey)) {
      series.fullTestsMap.set(fullTestKey, {
        groupKey: `${seriesKey}-${fullTestKey}`,
        groupId,
        groupName: getFullTestName(test, series.groupName),
        tests: [],
      });
    }
    series.fullTestsMap.get(fullTestKey).tests.push(test);
  });

  return Array.from(seriesMap.values()).map((series) => ({
    ...series,
    fullTests: Array.from(series.fullTestsMap.values()).sort(
      (a, b) => getFullTestSortNumber(b) - getFullTestSortNumber(a)
    ),
  }));
}

const IELTSDashboardV2 = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [open, setOpen] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKind, setSelectedKind] = useState(testFilters[0].value);
  const [selectedSection, setSelectedSection] = useState(sections[0].title);
  const [loadingTestId, setLoadingTestId] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(() => {
    const stored = localStorage.getItem('ielts-type');
    return stored ? stored.toLowerCase() : 'academic';
  });
  const [profileData, setProfileData] = useState({
    averageScore: '- / 9',
    targetScore: '- / 9',
    examIn: '- Days',
    loading: true,
    error: null,
  });
  const [showTargetScoreModal, setShowTargetScoreModal] = useState(false);
  const [showExamDateModal, setShowExamDateModal] = useState(false);
  const [newTargetScore, setNewTargetScore] = useState('');
  const [newExamDate, setNewExamDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isSubscribed = userData?.IsSubscribed || false;

  const fetchProfileData = useCallback(async () => {
    try {
      setProfileData((prev) => ({ ...prev, loading: true, error: null }));
      const response = await fetch(`${Base_URL}/app/users/get-ielts-profile`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to fetch profile data');
      const profile = data.response;
      const averageScore = formatIELTSBandScore(profile.average_score);
      setProfileData({
        averageScore: averageScore !== '-' ? `${averageScore} / 9` : '- / 9',
        targetScore: profile.ielts_target_score ? `${profile.ielts_target_score} / 9` : '- / 9',
        examIn: profile.days_until_exam ? `${String(profile.days_until_exam).padStart(2, '0')} Days` : '- Days',
        loading: false,
        error: null,
      });
    } catch (err) {
      setProfileData((prev) => ({ ...prev, loading: false, error: err.message }));
    }
  }, []);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const testsArr = Array.isArray(data) ? data : Array.isArray(data.response) ? data.response : [];
      setTests(testsArr);
    } catch (err) {
      setError('Failed to fetch IELTS tests');
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getUserTestsInfo`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      setResults(data.response?.tests || []);
    } catch (err) {
      setResults([]);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    setOpen(isNotChrome());
    const localUserData = JSON.parse(localStorage.getItem('userData') || '{}');
    setShowFooter((localUserData?.PortalId || 0) === 1);
    localStorage.setItem('exam-type', 'ielts');
    if (!localStorage.getItem('ielts-type')) localStorage.setItem('ielts-type', 'academic');
    window.dispatchEvent(new CustomEvent('examTypeChanged', { detail: { examType: 'ielts' } }));

    fetchTests();
    fetchProfileData();
    fetchResults();

    const handleIeltsTypeChange = (event) => {
      const { ieltsType } = event.detail || {};
      if (ieltsType) setSelectedTestType(ieltsType.toLowerCase());
    };
    window.addEventListener('ieltsTypeChanged', handleIeltsTypeChange);
    return () => window.removeEventListener('ieltsTypeChanged', handleIeltsTypeChange);
  }, [fetchTests, fetchProfileData, fetchResults]);

  const sectionCounts = useMemo(() => {
    const counts = { Listening: 0, Reading: 0, Writing: 0, Speaking: 0 };
    tests.forEach((test) => {
      const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
      if (!matchesType || !test.category_name) return;
      const category = test.category_name.toLowerCase();
      if (category.includes('listening')) counts.Listening += 1;
      else if (category.includes('reading')) counts.Reading += 1;
      else if (category.includes('writing')) counts.Writing += 1;
      else if (category.includes('speaking')) counts.Speaking += 1;
    });
    return counts;
  }, [tests, selectedTestType]);

  const matchesKind = useCallback((test) => {
    if (selectedKind === 'All') return true;
    return test.test_kind ? test.test_kind.toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase()) : false;
  }, [selectedKind]);

  const matchesType = useCallback(
    (test) => (test.test_type ? test.test_type.toLowerCase() === selectedTestType : true),
    [selectedTestType]
  );

  const fullMockSeries = useMemo(() => {
    const groupedSeries = groupFullTestsBySeries(
      tests.filter((test) => getGroupId(test) && matchesKind(test) && matchesType(test))
    );
    let unlockIndex = 0;

    return groupedSeries.map((series) => ({
      ...series,
      fullTests: series.fullTests.map((fullTest) => {
        const categories = [...new Set(fullTest.tests.map((test) => test.category_name))].filter(Boolean);
        const representative = fullTest.tests[0];
        const totalDuration = fullTest.tests.reduce((sum, test) => sum + (test.total_time || 0), 0);
        const requiredCategories = ['Listening', 'Reading', 'Writing'];
        const lowerCategories = categories.map((category) => category.toLowerCase());
        const missingCategories = requiredCategories.filter((category) => !lowerCategories.includes(category.toLowerCase()));
        const currentUnlockIndex = unlockIndex;
        unlockIndex += 1;

        return {
          ...fullTest,
          representative,
          categories,
          totalDuration,
          missingCategories,
          hasAllRequiredCategories: missingCategories.length === 0,
          isUnlocked: isSubscribed ? true : currentUnlockIndex === 0,
          hasInProgress: fullTest.tests.some((test) => test.has_in_progress_full_attempt || test.resume_full_test_attempt_id),
          latestSummary: fullTest.tests.find((test) => test.latest_full_attempt_summary)?.latest_full_attempt_summary || null,
          resumeAttemptId: fullTest.tests.find((test) => test.resume_full_test_attempt_id)?.resume_full_test_attempt_id || null,
        };
      }),
    }));
  }, [tests, matchesKind, matchesType, isSubscribed]);

  const sectionalGroups = useMemo(() => {
    const filtered = tests.filter((test) => {
      const sectionMatch = test.category_name?.toLowerCase() === selectedSection.toLowerCase();
      return sectionMatch && matchesKind(test) && matchesType(test);
    });
    return groupByPrefix(filtered, true).map((group, groupIndex) => ({
      ...group,
      tests: group.tests.map((test, testIndex) => ({
        ...test,
        isUnlocked: isSubscribed ? true : groupIndex === 0 && testIndex === 0,
      })),
    }));
  }, [tests, matchesKind, matchesType, selectedSection, isSubscribed]);

  const lastFiveResults = useMemo(() => (results || []).slice(0, 5), [results]);

  const navigateToResultDetails = (result) => {
    if (result?.test_type === 'full' || result?.full_test_attempt_id || result?.fullTestAttemptId) {
      const fullTestAttemptId = result.full_test_attempt_id || result.fullTestAttemptId;
      if (fullTestAttemptId) {
        navigate(`/ielts-test-details?type=full&fullTestAttemptId=${fullTestAttemptId}`);
        return;
      }
    }

    const attemptId = result?.attempt_id || result?.attemptId;
    if (attemptId) {
      navigate(`/ielts-test-details?type=individual&attemptId=${attemptId}`);
      return;
    }

    navigate('/ielts-results');
  };

  const getSectionIcon = (iconName) => {
    switch (iconName) {
      case 'listening': return <ListeningIcon sx={{ fontSize: 22 }} />;
      case 'reading': return <ReadingIcon sx={{ fontSize: 22 }} />;
      case 'writing': return <WritingIcon sx={{ fontSize: 22 }} />;
      case 'speaking': return <SpeakingIcon sx={{ fontSize: 22 }} />;
      default: return null;
    }
  };

  const resolveFullTestResumeAttemptId = (group) => Number(
    group.resumeAttemptId || group.latestSummary?.full_test_attempt_id || 0
  );

  const handleFullTestResume = async (group) => {
    const attemptId = resolveFullTestResumeAttemptId(group);
    if (!attemptId) {
      alert('No in-progress attempt to resume.');
      return;
    }
    const loadingKey = `full-${group.groupKey}-resume`;
    setLoadingTestId(loadingKey);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/full-attempts/${attemptId}/resume-context`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const payload = data?.response;
      if (!response.ok || data?.responseCode !== 200 || !payload?.tests?.length) {
        throw new Error(data?.message || 'Unable to load full test for resume');
      }
      navigate('/ielts-full-test', {
        state: {
          mode: 'practice',
          groupedTests: group.tests,
          groupId: Number(group.groupId),
          continuePrevious: true,
          resumeFullTestAttemptId: Number(attemptId),
          prefetchedResumeContext: { ...payload, fullTestAttemptId: Number(attemptId) },
        },
      });
    } catch (err) {
      alert(err.message || 'Unable to resume full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleFullTestStart = async (group) => {
    const shouldResume = Boolean(group.hasInProgress || String(group.latestSummary?.status || '').toLowerCase() === 'in_progress');
    const loadingKey = `full-${group.groupKey}-start`;
    setLoadingTestId(loadingKey);
    try {
      if (shouldResume) {
        const response = await fetch(`${Base_URL}/app/users/ielts-tests/full-attempts/start`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId: group.groupId }),
        });
        const data = await response.json();
        if (data.responseCode !== 200 || !data.response?.fullTestAttemptId) {
          throw new Error(data.message || 'Could not start a new attempt');
        }
        navigate('/ielts-full-test', {
          state: {
            mode: 'practice',
            groupedTests: group.tests,
            groupId: group.groupId,
            continuePrevious: false,
            fullTestAttemptId: data.response.fullTestAttemptId,
          },
        });
        return;
      }
      navigate('/ielts-full-test', {
        state: { mode: 'practice', groupedTests: group.tests, groupId: group.groupId },
      });
    } catch (err) {
      alert(err.message || 'Failed to start full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleStartTest = async (testId, mode = 'practice') => {
    const loadingId = `sectional-${testId}`;
    setLoadingTestId(loadingId);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getTestById/${testId}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const test = data.response;
      if (!response.ok || !test) throw new Error('Test not found');

      const state = { duration: test.total_time || 60, mode, testData: test };
      if (test.category_name === 'Listening') navigate('/ielts-listening-test', { state: { ...state, duration: 30 } });
      else if (test.category_name === 'Writing') navigate('/ielts-writing-test', { state: { ...state, duration: 60 } });
      else if (test.category_name === 'Reading') navigate('/ielts-reading-test', { state: { ...state, duration: 60 } });
      else if (test.category_name === 'Speaking') navigate('/ielts-speaking-test', { state: { ...state, duration: 15, currentUserId: userData?.UserId || 1 } });
    } catch (err) {
      alert('Failed to load test details. Please try again.');
    } finally {
      setLoadingTestId(null);
    }
  };

  const updateTargetScore = async () => {
    if (!newTargetScore) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ielts_target_score: parseFloat(newTargetScore) }),
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to update target score');
      await fetchProfileData();
      setShowTargetScoreModal(false);
      setNewTargetScore('');
    } catch (err) {
      alert('Failed to update target score. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const updateExamDate = async () => {
    if (!newExamDate) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ielts_exam_date: newExamDate }),
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to update exam date');
      await fetchProfileData();
      setShowExamDateModal(false);
      setNewExamDate('');
    } catch (err) {
      alert('Failed to update exam date. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const renderSidebar = () => (
    <SidebarCard>
      <NavButton active={activeView === 'dashboard'} onClick={() => setActiveView('dashboard')}>
        Dashboard
      </NavButton>
      <NavButton active={false} onClick={() => navigate('/ielts-results')}>
        Results
      </NavButton>
      <Divider sx={{ my: 1 }} />
      <NavButton active={activeView === 'sectional'} onClick={() => setActiveView('sectional')}>
        Sectional Mock Test
      </NavButton>
      <NavButton active={activeView === 'full'} onClick={() => setActiveView('full')}>
        Full Mock Test
      </NavButton>
    </SidebarCard>
  );

  const renderFilterRow = () => (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {testFilters.map((filter) => (
        <Button
          key={filter.value}
          variant={selectedKind === filter.value ? 'contained' : 'outlined'}
          onClick={() => setSelectedKind(filter.value)}
          size="small"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: selectedKind === filter.value ? themeColors.primary : '#fff',
            borderColor: 'rgba(153,108,254,0.35)',
            color: selectedKind === filter.value ? '#fff' : '#4b3d72',
            '&:hover': {
              backgroundColor: selectedKind === filter.value ? themeColors.primaryHover : '#f7f1ff',
              borderColor: themeColors.primary,
            },
          }}
        >
          {filter.label}
        </Button>
      ))}
    </Stack>
  );

  const renderScoreCards = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <ScoreCard title="Average Score" score={profileData.loading ? 'Loading...' : profileData.error ? 'Error' : profileData.averageScore} icon={<CalculatorIcon sx={{ fontSize: 24, color: '#FF6B6B' }} />} bgcolor="#FFEAEA" />
      </Grid>
      <Grid item xs={12} md={4}>
        <ScoreCard title="Target Score" score={profileData.loading ? 'Loading...' : profileData.error ? 'Error' : profileData.targetScore} icon={<TargetIcon sx={{ fontSize: 24, color: themeColors.primary }} />} bgcolor={themeColors.primaryLighter} showChangeButton onChangeClick={() => setShowTargetScoreModal(true)} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ScoreCard title="Exam In" score={profileData.loading ? 'Loading...' : profileData.error ? 'Error' : profileData.examIn} icon={<CalendarIcon sx={{ fontSize: 24, color: '#00B4D8' }} />} bgcolor="#E1F9FF" showChangeButton onChangeClick={() => setShowExamDateModal(true)} />
      </Grid>
    </Grid>
  );

  const renderRecentResults = () => (
    <GroupCard>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#221a3a' }}>
          Last 5 Test Results
        </Typography>
        <Button onClick={() => navigate('/ielts-results')} size="small" sx={{ textTransform: 'none', fontWeight: 800 }}>
          View all
        </Button>
      </Box>
      {resultsLoading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
      ) : lastFiveResults.length === 0 ? (
        <Typography color="text.secondary">No results yet.</Typography>
      ) : (
        <Stack spacing={1}>
          {lastFiveResults.map((result, index) => (
            <Paper
              key={result.testId || result.testid || result.attempt_id || result.full_test_attempt_id || index}
              onClick={() => navigateToResultDetails(result)}
              sx={{
                p: 1.2,
                borderRadius: '12px',
                border: '1px solid #eef0f7',
                cursor: 'pointer',
                '&:hover': { borderColor: '#996CFE', background: '#fbf8ff' },
              }}
            >
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#2d2547' }}>{getTestName(result)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getTestTypeLabel(result)} - {formatDate(result.date_time)}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Chip label={result.status || 'completed'} size="small" sx={{ mb: 0.4, fontWeight: 700 }} />
                  <Typography variant="body2" sx={{ fontWeight: 900, color: '#996CFE' }}>
                    {formatResultScore(result)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </GroupCard>
  );

  const renderDashboard = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: '#211936' }}>
          IELTS Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
          Track your target, exam timeline, and latest IELTS test activity.
        </Typography>
      </Box>
      {renderScoreCards()}
      {renderRecentResults()}
    </>
  );

  const renderFullTests = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 900, color: '#211936', mb: 0.4 }}>
        Full Mock Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Full mocks are grouped by test type and the first two digits of group ID.
      </Typography>
      {renderFilterRow()}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : fullMockSeries.length === 0 ? (
        <Typography color="text.secondary">No full mock tests available.</Typography>
      ) : (
        fullMockSeries.map((series) => (
          <GroupCard key={`full-series-${series.groupKey}`}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={0.5}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#2d2547' }}>{series.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {series.fullTests.length} full mock tests
                </Typography>
              </Box>
              <Chip label={`Group ${series.groupPrefix}`} size="small" sx={{ fontWeight: 800 }} />
            </Box>
            <TestGrid>
              {series.fullTests.map((group) => {
                const disabledMessage = group.hasAllRequiredCategories ? null : `Under development. ${group.missingCategories.join(', ')} sections are being added.`;
                const shouldResume = Boolean(group.hasInProgress || String(group.latestSummary?.status || '').toLowerCase() === 'in_progress');
                return (
                  <FullTestItem key={`full-${group.groupKey}`}>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {group.categories.map((category) => <Chip key={category} label={category} size="small" />)}
                    </Stack>
                    <TestCard
                      title={group.groupName}
                      time={formatTime(group.totalDuration, '2h:30m')}
                      difficulty={group.representative?.test_type || 'N/A'}
                      onStart={() => handleFullTestStart(group)}
                      onResume={() => handleFullTestResume(group)}
                      showResumeButton={shouldResume}
                      loadingStart={loadingTestId === `full-${group.groupKey}-start`}
                      loadingResume={loadingTestId === `full-${group.groupKey}-resume`}
                      isLocked={!group.isUnlocked}
                      disabledMessage={disabledMessage}
                    />
                  </FullTestItem>
                );
              })}
            </TestGrid>
          </GroupCard>
        ))
      )}
    </>
  );

  const renderSectionalTests = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 900, color: '#211936', mb: 0.4 }}>
        Sectional Mock Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sectional tests are grouped by test type and the first two digits of group ID.
      </Typography>
      {renderFilterRow()}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {sections.map((section) => (
          <Grid item xs={6} md={3} key={section.id}>
            <SectionCard
              title={section.title}
              count={sectionCounts[section.title] || 0}
              icon={getSectionIcon(section.icon)}
              onClick={() => setSelectedSection(section.title)}
              isSelected={selectedSection === section.title}
            />
          </Grid>
        ))}
      </Grid>
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : sectionalGroups.length === 0 ? (
        <Typography color="text.secondary">No sectional tests available.</Typography>
      ) : (
        sectionalGroups.map((group) => (
          <GroupCard key={`sectional-${group.groupKey}`}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={1}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#2d2547' }}>{group.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.groupKey === 'individual' ? 'Standalone tests' : `Group prefix: ${group.groupPrefix}`}
                </Typography>
              </Box>
              <Chip label={selectedSection} color="primary" size="small" />
            </Box>
            <TestGrid>
              {group.tests.map((test) => {
                const testName = getTestName(test);
                return (
                  <SectionalTestCard
                    key={test.id}
                    title={testName}
                    time={formatTime(test.total_time, selectedSection === 'Reading' || selectedSection === 'Writing' ? '01:00h' : selectedSection === 'Speaking' ? '00:15m' : '00:30m')}
                    difficulty={test.test_type || 'N/A'}
                    onStart={() => handleStartTest(test.id)}
                    selectedSection={selectedSection}
                    isLocked={!test.isUnlocked}
                    loading={loadingTestId === `sectional-${test.id}`}
                  />
                );
              })}
            </TestGrid>
          </GroupCard>
        ))
      )}
    </>
  );

  const renderTargetScoreModal = () => (
    <Dialog open={showTargetScoreModal} onClose={() => setShowTargetScoreModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', color: themeColors.primary, fontWeight: 800 }}>Update Target Score</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Target Score</InputLabel>
          <Select value={newTargetScore} label="Target Score" onChange={(event) => setNewTargetScore(event.target.value)}>
            {[4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0].map((score) => (
              <MenuItem key={score} value={score}>{score.toFixed(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={updateTargetScore} variant="contained" disabled={!newTargetScore || isUpdating}>Update</Button>
        <Button onClick={() => setShowTargetScoreModal(false)} disabled={isUpdating}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const renderExamDateModal = () => (
    <Dialog open={showExamDateModal} onClose={() => setShowExamDateModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', color: themeColors.primary, fontWeight: 800 }}>Update Exam Date</DialogTitle>
      <DialogContent>
        <TextField
          label="Exam Date"
          type="date"
          value={newExamDate}
          onChange={(event) => setNewExamDate(event.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={updateExamDate} variant="contained" disabled={!newExamDate || isUpdating}>Update</Button>
        <Button onClick={() => setShowExamDateModal(false)} disabled={isUpdating}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      <BrowserNotSupported open={open} close={setOpen} />
      <Navbar />
      <PageShell>
        <DashboardContainer maxWidth="xl">
          {renderSidebar()}
          <MainCard>
            {activeView === 'dashboard' && renderDashboard()}
            {activeView === 'sectional' && renderSectionalTests()}
            {activeView === 'full' && renderFullTests()}
          </MainCard>
        </DashboardContainer>
      </PageShell>
      {showFooter && (
        <Box sx={{ padding: '0rem 3% 0rem', background: 'white' }}>
          <Footer />
        </Box>
      )}
      {renderTargetScoreModal()}
      {renderExamDateModal()}
    </Box>
  );
};

export default IELTSDashboardV2;
